import React, { useState } from "react";
import { endpoints } from "@/lib/api";
import { Bell, BellOff, Clock } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const PRESETS = [
  { label: "Morning · 08:00", value: "08:00" },
  { label: "Noon · 12:00", value: "12:00" },
  { label: "Evening · 20:00", value: "20:00" },
];

export default function ReminderPill({ ritual, onChange }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [custom, setCustom] = useState(ritual.remind_at || "");

  const label = ritual.remind_at ? ritual.remind_at : "No reminder";

  const save = async (value) => {
    setPending(true);
    try {
      const { data } = await endpoints.updateReminder(ritual.id, value);
      onChange && onChange(data);
      setOpen(false);
      toast.success(value ? `Reminder set for ${value}` : "Reminder off");
    } catch { toast.error("Could not update reminder."); }
    setPending(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`reminder-open-${ritual.slug}`}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border hairline text-[11px] tab-link ${
            ritual.remind_at
              ? "bg-[hsl(var(--terracotta))]/15 text-[hsl(var(--fg))] border-[hsl(var(--terracotta))]/30"
              : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--sand))]"
          }`}
        >
          {ritual.remind_at ? <Bell size={11} /> : <BellOff size={11} />}
          <span className="tabular-nums">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 bg-[hsl(var(--paper))] border-[hsl(var(--border))]" data-testid={`reminder-popover-${ritual.slug}`}>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Remind me at</div>
        <div className="mt-3 space-y-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => save(p.value)}
              disabled={pending}
              data-testid={`reminder-preset-${ritual.slug}-${p.value.replace(":", "")}`}
              className={`w-full text-left px-3 py-1.5 rounded-md text-sm tab-link ${
                ritual.remind_at === p.value ? "bg-[hsl(var(--terracotta))]/20" : "hover:bg-[hsl(var(--sand))]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t hairline">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">
            <Clock size={10} className="inline mr-1" /> Custom time
          </div>
          <div className="flex gap-2">
            <Input
              type="time"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              data-testid={`reminder-custom-input-${ritual.slug}`}
              className="bg-transparent"
            />
            <Button
              onClick={() => custom && save(custom)}
              disabled={pending || !custom}
              data-testid={`reminder-custom-save-${ritual.slug}`}
              size="sm"
              className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
            >
              Set
            </Button>
          </div>
        </div>
        {ritual.remind_at && (
          <button
            type="button"
            onClick={() => save(null)}
            disabled={pending}
            data-testid={`reminder-off-${ritual.slug}`}
            className="mt-3 w-full text-left text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] tab-link"
          >
            Turn reminder off
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
